// Combat sound effects (Matt, 2026-10-15: "attacks need sound effects"). Synthesised with WebAudio, so there are no
// files to fetch and every hit can vary a little: a swing is a swept noise whoosh, a caster's attack a falling zap,
// a hit a low thump with a short crack on top (louder on a crit), a hit on YOU a duller thud, a zombie winding up a
// low growl, a zombie going down a long soft fall. Sounds from other people are quieter with distance and panned
// to where they are on screen. The SOUND button (and M) mutes these with the music.
import { MUSIC } from './music';

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let noiseBuf: AudioBuffer | null = null;
let vol = 0.55;                       // OPTIONS: the effects volume
const last = new Map<string, number>();
let eng: { c: AudioContext; o: OscillatorNode; o2: OscillatorNode; g: GainNode } | null = null;
let ten: { o: OscillatorNode; o2: OscillatorNode; g: GainNode } | null = null;

function ac(): AudioContext | null {
  if (MUSIC.muted || !MUSIC.unlocked) return null;
  if (!ctx) {
    try { ctx = new AudioContext(); } catch { return null; }
    master = ctx.createGain(); master.gain.value = vol; master.connect(ctx.destination);
    noiseBuf = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
    const d = noiseBuf.getChannelData(0); for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  }
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  return ctx;
}

/** gain (0..1 by distance) and pan (-1..1) for a sound at screen offset dx, dy px from you */
function place(dx = 0, dy = 0): { g: number; pan: number } {
  const d = Math.hypot(dx, dy);
  return { g: Math.max(0, 1 - d / 260), pan: Math.max(-0.8, Math.min(0.8, dx / 200)) };
}

function out(c: AudioContext, vol: number, pan: number): AudioNode {
  const g = c.createGain(); g.gain.value = vol;
  const p = c.createStereoPanner(); p.pan.value = pan;
  g.connect(p).connect(master!);
  return g;
}

function noise(c: AudioContext, dest: AudioNode, t0: number, dur: number, type: BiquadFilterType, f0: number, f1: number, q: number, a: number, attack = 0.005) {
  const src = c.createBufferSource(); src.buffer = noiseBuf; src.playbackRate.value = 0.8 + Math.random() * 0.4;
  const f = c.createBiquadFilter(); f.type = type; f.Q.value = q;
  f.frequency.setValueAtTime(f0, t0); f.frequency.exponentialRampToValueAtTime(Math.max(20, f1), t0 + dur);
  const g = c.createGain(); g.gain.setValueAtTime(0.0001, t0); g.gain.exponentialRampToValueAtTime(a, t0 + attack); g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  src.connect(f).connect(g).connect(dest);
  src.start(t0, Math.random() * 0.5); src.stop(t0 + dur + 0.02);
}

function tone(c: AudioContext, dest: AudioNode, t0: number, dur: number, type: OscillatorType, f0: number, f1: number, a: number, attack = 0.004) {
  const o = c.createOscillator(); o.type = type;
  o.frequency.setValueAtTime(f0, t0); o.frequency.exponentialRampToValueAtTime(Math.max(20, f1), t0 + dur);
  const g = c.createGain(); g.gain.setValueAtTime(0.0001, t0); g.gain.exponentialRampToValueAtTime(a, t0 + attack); g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  o.connect(g).connect(dest); o.start(t0); o.stop(t0 + dur + 0.02);
}

/** one sound per kind per ~40 ms, so a crowd of hits in one tick does not stack into a roar */
function gate(k: string, ms = 40): boolean {
  const n = performance.now(); if ((last.get(k) ?? 0) + ms > n) return false; last.set(k, n); return true;
}

export const SFX = {
  setVol(v: number) { vol = v; if (master) master.gain.value = v; },
  /** a melee swing */
  swing(dx = 0, dy = 0, vol = 1) {
    const c = ac(); if (!c || !gate('swing', 60)) return; const { g, pan } = place(dx, dy); if (g <= 0) return;
    const d = out(c, vol * g, pan), t = c.currentTime;
    const s = 0.9 + Math.random() * 0.25;
    noise(c, d, t, 0.16, 'bandpass', 500 * s, 2600 * s, 1.4, 0.5, 0.05);
  },
  /** a caster's SKILL (R1-002): Matt's road salt is a bright crystalline shimmer, Steve's power chord a distorted
   *  root and fifth through an amp; anyone else gets a fuller zap */
  cast(key: string, dx = 0, dy = 0) {
    const c = ac(); if (!c || !gate('cast:' + key, 120)) return; const { g, pan } = place(dx, dy); if (g <= 0) return;
    const d = out(c, 0.9 * g, pan), t = c.currentTime;
    if (key === 'matt') {
      noise(c, d, t, 0.35, 'highpass', 5000, 9000, 0.7, 0.25, 0.01);
      for (let i = 0; i < 7; i++) tone(c, d, t + i * 0.03, 0.12, 'sine', 2400 + Math.random() * 2600, 3000, 0.07);
    } else if (key === 'steve') {
      const ws = c.createWaveShaper(); const cv = new Float32Array(256); for (let i = 0; i < 256; i++) { const x = i / 128 - 1; cv[i] = Math.tanh(x * 6); } ws.curve = cv; ws.connect(d);
      for (const f of [110, 165, 220]) { const o = c.createOscillator(); o.type = 'sawtooth'; o.frequency.value = f; const gg = c.createGain(); gg.gain.setValueAtTime(0.0001, t); gg.gain.exponentialRampToValueAtTime(0.18, t + 0.02); gg.gain.exponentialRampToValueAtTime(0.0001, t + 0.55); o.connect(gg).connect(ws); o.start(t); o.stop(t + 0.6); }
    } else { tone(c, d, t, 0.3, 'sawtooth', 1600, 200, 0.14); noise(c, d, t, 0.15, 'highpass', 3000, 6000, 0.7, 0.15); }
  },
  /** a caster's attack: a falling zap with a fizz */
  zap(dx = 0, dy = 0, vol = 1) {
    const c = ac(); if (!c || !gate('zap', 60)) return; const { g, pan } = place(dx, dy); if (g <= 0) return;
    const d = out(c, vol * g, pan), t = c.currentTime;
    tone(c, d, t, 0.22, 'sawtooth', 1400 + Math.random() * 200, 260, 0.12);
    tone(c, d, t, 0.18, 'square', 700, 140, 0.05);
    noise(c, d, t, 0.12, 'highpass', 3000, 6000, 0.7, 0.12);
  },
  /** something got hit: a thump and a crack; heavier on a crit */
  hit(dx = 0, dy = 0, crit = false) {
    const c = ac(); if (!c || !gate('hit')) return; const { g, pan } = place(dx, dy); if (g <= 0) return;
    const d = out(c, (crit ? 1 : 0.8) * g, pan), t = c.currentTime;
    tone(c, d, t, crit ? 0.2 : 0.14, 'sine', crit ? 170 : 150, 45, 0.9);
    noise(c, d, t, crit ? 0.12 : 0.07, 'lowpass', crit ? 3200 : 2200, 400, 0.8, crit ? 0.8 : 0.55);
    if (crit) noise(c, d, t + 0.01, 0.05, 'highpass', 2500, 5000, 0.8, 0.35);
  },
  /** the hearing's bells (studio 008, 008-hearing.md 1): one as Malrec starts listening, two 2 s before he reads. A struck
   *  bell: a bright fundamental and two inharmonic partials, a long decay, the second strike 0.45 s after the first */
  bell(n = 1) {
    const c = ac(); if (!c || !gate('bell', 300)) return;
    const d = out(c, 0.7, 0), t = c.currentTime;
    for (let k = 0; k < n; k++) for (const [f, a, dur] of [[880, 0.35, 1.6], [2210, 0.12, 0.9], [3490, 0.06, 0.5]] as [number, number, number][]) tone(c, d, t + k * 0.45, dur, 'sine', f, f * 0.998, a, 0.002);
  },
  /** you got hit: duller and lower, so it reads as YOU */
  hurt() {
    const c = ac(); if (!c || !gate('hurt', 80)) return;
    const d = out(c, 0.9, 0), t = c.currentTime;
    tone(c, d, t, 0.2, 'triangle', 210, 70, 0.7);
    noise(c, d, t, 0.12, 'lowpass', 900, 180, 0.9, 0.6);
  },
  /** a zombie winding up to bite: a short low growl (the tell you can step out of) */
  growl(dx = 0, dy = 0) {
    const c = ac(); if (!c || !gate('growl', 150)) return; const { g, pan } = place(dx, dy); if (g <= 0) return;
    const d = out(c, 0.35 * g, pan), t = c.currentTime;
    const o = c.createOscillator(); o.type = 'sawtooth'; o.frequency.setValueAtTime(95 + Math.random() * 20, t); o.frequency.linearRampToValueAtTime(70, t + 0.3);
    const lfo = c.createOscillator(); lfo.frequency.value = 28; const lg = c.createGain(); lg.gain.value = 18; lfo.connect(lg).connect(o.frequency);
    const f = c.createBiquadFilter(); f.type = 'lowpass'; f.frequency.value = 600;
    const gg = c.createGain(); gg.gain.setValueAtTime(0.0001, t); gg.gain.exponentialRampToValueAtTime(0.5, t + 0.04); gg.gain.exponentialRampToValueAtTime(0.0001, t + 0.32);
    o.connect(f).connect(gg).connect(d); o.start(t); lfo.start(t); o.stop(t + 0.34); lfo.stop(t + 0.34);
  },
  /** something in the world took a hit: glass cracks and shatters, wood thunks and splinters, metal clangs, brick thuds */
  smash(cls: string, broke: boolean, dx = 0, dy = 0) {
    const c = ac(); if (!c || !gate('smash:' + cls, 70)) return; const { g, pan } = place(dx, dy); if (g <= 0) return;
    const d = out(c, (broke ? 0.9 : 0.6) * g, pan), t = c.currentTime;
    if (cls === 'glass') {
      noise(c, d, t, broke ? 0.45 : 0.08, 'highpass', 4000, 7000, 0.6, broke ? 0.5 : 0.35);
      for (let i = 0; broke && i < 6; i++) tone(c, d, t + 0.02 + Math.random() * 0.25, 0.06, 'sine', 2600 + Math.random() * 3000, 2000, 0.08);
    } else if (cls === 'metal') {
      tone(c, d, t, broke ? 0.5 : 0.3, 'triangle', 520 + Math.random() * 80, 480, 0.25); tone(c, d, t, 0.25, 'square', 1310, 1200, 0.05);
      noise(c, d, t, 0.06, 'bandpass', 3000, 2000, 2, 0.3);
    } else if (cls === 'wood') {
      tone(c, d, t, 0.12, 'sine', 220, 90, 0.7); noise(c, d, t, broke ? 0.3 : 0.08, 'bandpass', 900, 500, 1.2, broke ? 0.6 : 0.4);
      if (broke) noise(c, d, t + 0.05, 0.2, 'highpass', 2000, 3000, 0.8, 0.25);
    } else {
      tone(c, d, t, 0.18, 'sine', 120, 40, 0.8); noise(c, d, t, broke ? 0.5 : 0.1, 'lowpass', 1200, 200, 0.7, broke ? 0.6 : 0.4);
    }
  },
  /** loot hits the floor: a soft drop, with a rising chime for the good stuff (Diablo: you HEAR a rare) */
  drop(rarity: number, dx = 0, dy = 0) {
    const c = ac(); if (!c || !gate('drop', 60)) return; const { g, pan } = place(dx, dy); if (g <= 0) return;
    const d = out(c, 0.7 * g, pan), t = c.currentTime;
    tone(c, d, t, 0.08, 'sine', 300, 160, 0.4); noise(c, d, t, 0.05, 'bandpass', 1500, 800, 2, 0.2);
    const notes = [[], [], [880], [880, 1320], [660, 990, 1320], [523, 784, 1047, 1568]][Math.max(0, Math.min(5, rarity))];
    notes.forEach((f, i) => tone(c, d, t + 0.09 + i * 0.07, 0.35, 'triangle', f, f * 0.995, 0.12));
  },
  /** you picked something up */
  pickup() {
    const c = ac(); if (!c || !gate('pickup', 80)) return;
    const d = out(c, 0.5, 0), t = c.currentTime;
    tone(c, d, t, 0.07, 'square', 600, 900, 0.08); tone(c, d, t + 0.05, 0.08, 'square', 900, 1200, 0.06);
  },
  /** one of the turned moaning (R2-003: danger was silent until it bit): a low throat sound with a slow pitch sag,
   *  heard from a little off-screen (falloff 420 px instead of 260) */
  moan(dx = 0, dy = 0, vol = 1) {
    const c = ac(); if (!c || !gate('moan', 350)) return;
    const d0 = Math.hypot(dx, dy), g = Math.max(0, 1 - d0 / 420) * vol; if (g <= 0.02) return;
    const d = out(c, 0.32 * g, Math.max(-0.8, Math.min(0.8, dx / 220))), t = c.currentTime, dur = 0.7 + Math.random() * 0.6;
    const o = c.createOscillator(); o.type = 'sawtooth'; const f0 = 95 + Math.random() * 40;
    o.frequency.setValueAtTime(f0, t); o.frequency.linearRampToValueAtTime(f0 * 1.12, t + dur * 0.3); o.frequency.linearRampToValueAtTime(f0 * 0.8, t + dur);
    const f = c.createBiquadFilter(); f.type = 'bandpass'; f.Q.value = 3; f.frequency.setValueAtTime(520 + Math.random() * 200, t); f.frequency.linearRampToValueAtTime(380, t + dur);
    const gg = c.createGain(); gg.gain.setValueAtTime(0.0001, t); gg.gain.exponentialRampToValueAtTime(0.6, t + 0.15); gg.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(f).connect(gg).connect(d); o.start(t); o.stop(t + dur + 0.05);
    noise(c, d, t, dur * 0.8, 'bandpass', 800, 400, 2, 0.08, 0.1);
  },
  /** the engine while you drive: a hum whose pitch follows the speed (on false stops it) */
  engine(on: boolean, kmh = 0) {
    if (MUSIC.muted) on = false;
    const c = on ? ac() : ctx;
    if (!on || !c) { if (eng) { const e = eng; eng = null; try { e.g.gain.setTargetAtTime(0.0001, e.c.currentTime, 0.1); setTimeout(() => { e.o.stop(); e.o2.stop(); }, 400); } catch { /* */ } } return; }
    if (!eng) {
      const o = c.createOscillator(), o2 = c.createOscillator(), f = c.createBiquadFilter(), g = c.createGain();
      o.type = 'sawtooth'; o2.type = 'square'; f.type = 'lowpass'; f.frequency.value = 420; g.gain.value = 0.0001;
      o.connect(f); o2.connect(f); f.connect(g).connect(master!); o.start(); o2.start();
      eng = { c, o, o2, g };
    }
    const rpm = 38 + kmh * 1.6;
    eng.o.frequency.setTargetAtTime(rpm, c.currentTime, 0.15); eng.o2.frequency.setTargetAtTime(rpm * 0.5, c.currentTime, 0.15);
    eng.g.gain.setTargetAtTime(0.07 + Math.min(0.08, kmh / 600), c.currentTime, 0.2);
  },
  /** a low drone that rises as the turned close in on you (0 calm .. 1 surrounded) */
  tension(level: number) {
    if (MUSIC.muted) level = 0;
    const c = level > 0.02 ? ac() : ctx; if (!c) return;
    if (!ten) {
      if (level <= 0.02) return;
      const o = c.createOscillator(), o2 = c.createOscillator(), g = c.createGain();
      o.type = 'sine'; o.frequency.value = 55; o2.type = 'sine'; o2.frequency.value = 58.3; g.gain.value = 0.0001;
      o.connect(g); o2.connect(g); g.connect(master!); o.start(); o2.start(); ten = { o, o2, g };
    }
    ten.g.gain.setTargetAtTime(0.0001 + 0.16 * Math.min(1, level), c.currentTime, 0.6);
  },
  /** a zombie goes down */
  down(dx = 0, dy = 0) {
    const c = ac(); if (!c || !gate('down', 80)) return; const { g, pan } = place(dx, dy); if (g <= 0) return;
    const d = out(c, 0.7 * g, pan), t = c.currentTime;
    tone(c, d, t + 0.05, 0.3, 'sine', 90, 38, 0.8);
    noise(c, d, t + 0.05, 0.35, 'lowpass', 700, 120, 0.7, 0.45, 0.02);
  },
};
